import { Button, Typography } from '@breetometer/components';
import { CopyIcon, StarRatingIcon, StatusIcon } from 'BreetAssets';
import { EngagementActions, Loader, ModalParts, RowSection, ScreenShotHeader } from 'BreetComponents';
import {
	alwaysRefetchApiQueryOptions,
	capitalizeFirstLetter,
	copyToClipboard,
	type CURRENCYCODETYPE,
	CURRENCYCONFIG,
	formatCurrencyAmount,
	formatDateTime,
	getTransactionStatus,
	mapArrayFromLength,
} from 'BreetHelpers';
import { useCurrencyOutput, useRatingsApi } from 'BreetHooks';
import {
	closeModal,
	openModal,
	selectFiatMeta,
	selectModals,
	useAppDispatch,
	useAppSelector,
	useGetTransactionSellServicesByIdQuery,
} from 'BreetRedux';
import { useCallback } from 'react';

const DialogTransactionDetails = () => {
	const modals = useAppSelector(selectModals);

	const dispatch = useAppDispatch();

	const fiatMeta = useAppSelector(selectFiatMeta);

	const currencyOutput = useCurrencyOutput();

	const modalMetaData = modals.find((i) => i.id === 'transactionDetails')?.metadata as { id: string } | undefined;

	const { isLoading, data: transactionData } = useGetTransactionSellServicesByIdQuery(modalMetaData?.id ?? '', {
		skip: !modalMetaData?.id,
		...alwaysRefetchApiQueryOptions,
	});

	const transaction = transactionData?.data;

	const ratingsApi = useRatingsApi(transaction?.rating);

	const resolveTrade = useCallback(() => {
		dispatch(openModal({ id: 'resolveTrade', metadata: { transactionId: transaction?._id } }));
	}, [dispatch, transaction?._id]);

	const exitModal = useCallback(() => {
		dispatch(closeModal('transactionDetails'));
	}, [dispatch]);

	const successReason = transaction?.doc.isCompletedWithFee ?? !!transaction?.doc.resolvedBy;

	const rateTransaction = useCallback(() => {
		if (transaction?.rating) return;

		dispatch(openModal({ id: 'ratings', metadata: { id: transaction?.doc._id } }));
	}, [dispatch, transaction?.doc._id, transaction?.rating]);

	const fiatConfig = CURRENCYCONFIG[transaction?.currency.toUpperCase() as CURRENCYCODETYPE];

	if (isLoading) return <Loader />;

	return (
		<ModalParts.Wrapper
			title='Transaction Details'
			rightSheet
			handleBackLink={exitModal}
		>
			<div className='transactiondetails bg-primary-500'>
				<div
					className='transactiondetails_content scrollbar'
					id='transaction-details-section'
				>
					<ScreenShotHeader title='Transaction Details' />

					<div className='transactiondetails_header'>
						<Typography as='p'>
							{transaction?.doc.assetDoc.name} ({transaction?.doc.assetDoc.symbol})
						</Typography>
						<Typography as='h5'>
							{transaction?.doc.cryptoReceived} {transaction?.doc.assetDoc.symbol}
						</Typography>
					</div>

					<div className='transactiondetails_itemscontainer bg-primary-500'>
						<RowSection.Tuple
							leftLabel='USD Amount'
							leftComponent={
								<div className='transactiondetails_value'>
									<Typography className='color-neutral-50'>${currencyOutput(transaction?.doc.amountReceived)}</Typography>
								</div>
							}
							rightLabel='Rate'
							hideRight={transaction?.currency.toUpperCase() === CURRENCYCONFIG.USD.code}
							rightComponent={
								<div className='transactiondetails_value'>
									<Typography className='color-neutral-50'>
										{fiatMeta.currencySymbol}
										{currencyOutput((transaction?.doc.rate ?? 0) / (transaction?.doc.conversionRate ?? 1))}/$
									</Typography>
								</div>
							}
						/>

						<RowSection.Tuple
							leftLabel='Payout Amount'
							leftComponent={
								<div className='transactiondetails_value'>
									<Typography className='color-neutral-50'>
										{formatCurrencyAmount(transaction?.amount ?? 0, transaction?.currency.toUpperCase() as CURRENCYCODETYPE)}
									</Typography>
								</div>
							}
							rightLabel='Confirmations'
							rightComponent={
								<div className='transactiondetails_value'>
									<Typography className='color-neutral-50'>
										{transaction?.doc.data.numOfConfirmations}/{transaction?.doc.assetDoc.confirmations}
									</Typography>
								</div>
							}
						/>

						<RowSection.Tuple
							leftLabel='Transaction Fee'
							leftComponent={
								<div className='transactiondetails_value'>
									<Typography className='color-neutral-50 transaction-fee-text'>
										{transaction?.doc.platformFeePercent}% ({(transaction?.doc.platformFeePercent ?? 0) / 100})
									</Typography>
								</div>
							}
							rightLabel='Resolution Fee'
							hideRight={!transaction?.doc.isCompletedWithFee}
							rightComponent={
								<div className='transactiondetails_value'>
									<Typography className='color-neutral-50'>${transaction?.doc.flagFeeUSD}</Typography>
								</div>
							}
						/>

						<RowSection.Single
							label='Transaction ID'
							component={
								<div className='transactiondetails_value'>
									<Typography className='color-neutral-50'>{transaction?.doc._id}</Typography>
									<CopyIcon
										variant='colored'
										focusable
										onClick={copyToClipboard(transaction?.doc._id, 'Transaction ID copied successfully')}
										className='cursor_pointer hide-during-screenshot'
									/>
								</div>
							}
						/>

						<RowSection.Single
							label='Transaction Link'
							component={
								<div className='transactiondetails_value'>
									<Typography
										as='a'
										href={transaction?.doc.txLink}
										target='_blank'
										rel='noopener noreferrer'
									>
										{transaction?.doc.txLink}
									</Typography>
									<CopyIcon
										variant='colored'
										focusable
										onClick={copyToClipboard(transaction?.doc.txLink, 'Transaction Link copied successfully')}
										className='cursor_pointer hide-during-screenshot'
									/>
								</div>
							}
						/>

						<RowSection.Tuple
							leftLabel='Status'
							leftComponent={
								<div className='transactiondetails_value'>
									<StatusIcon status={getTransactionStatus(transaction?.status ?? 'pending')} />
								</div>
							}
							rightLabel='Points Received'
							rightComponent={
								<div className='transactiondetails_value'>
									<Typography className='color-neutral-50'>{transaction?.doc.pointsEarned?.toLocaleString()}</Typography>
								</div>
							}
							hideRight={!transaction?.doc.pointsEarned}
							adminNote={
								transaction?.doc.reason ? (
									<div className={`transactiondetailadminnote ${!successReason ? 'error' : 'success'}`}>
										<Typography as='h6'>Admin Note</Typography>
										<Typography>{capitalizeFirstLetter(transaction.doc.reason)}</Typography>
									</div>
								) : undefined
							}
						/>

						{transaction?.status === 'completed' ? (
							<RowSection.Tuple
								rowClass='hide-during-screenshot'
								leftLabel='Balance Before'
								leftComponent={
									<div className='transactiondetails_value'>
										<Typography className='color-neutral-50'>
											{fiatConfig.symbol}
											{currencyOutput(transaction.doc.balanceBefore)}
										</Typography>
									</div>
								}
								rightLabel='Balance After'
								rightComponent={
									<div className='transactiondetails_value'>
										<Typography className='color-neutral-50'>
											{fiatConfig.symbol}
											{currencyOutput(transaction.doc.balanceAfter)}
										</Typography>
									</div>
								}
							/>
						) : null}

						<RowSection.Single
							label='Date'
							component={
								<div className='transactiondetails_value'>
									<Typography className='color-neutral-50'>{formatDateTime(transaction?.doc.updatedAt)}</Typography>
								</div>
							}
						/>
					</div>

					<div className='transactiondetails_itemscontainer bg-primary-500'>
						{transaction?.status === 'completed' && (
							<div className='transactiondetails_experience hide'>
								<div className='transactiondetails_experience_left'>
									<Typography as='p'>Rate your experience</Typography>
								</div>
								<Button
									variant='native'
									className={`transactiondetails_experience_right${transaction.rating ? '' : ' cursor_pointer'}`}
									onMouseLeave={ratingsApi.onHover(null)}
									onClick={rateTransaction}
								>
									{mapArrayFromLength(5).map((i, index) => {
										const isFilled = transaction.rating ? index + 1 <= transaction.rating : ratingsApi.checkIsFilled(index);

										return (
											<StarRatingIcon
												key={i}
												variant={isFilled ? 'filled' : 'default'}
												onMouseEnter={ratingsApi.onHover(index)}
											/>
										);
									})}
								</Button>
							</div>
						)}
					</div>
				</div>

				<EngagementActions
					intercomOpenMessage={`I have an issue with this withdrawal: ${transaction?.doc._id}`}
					shareContentDetails={{
						text: 'Transaction Detail Screenshot',
						title: 'Transaction Receipt',
						url: 'https://breet.io',
						screenshotElementId: 'transaction-details-section',
						action: 'share',
					}}
					isResolve={transaction?.status === 'flagged'}
					onResolve={resolveTrade}
				/>
			</div>
		</ModalParts.Wrapper>
	);
};

export default DialogTransactionDetails;
